import { ChevronDown, FileSpreadsheet, LoaderCircle, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  apiClient,
  type ExportColumn,
  type ExportColumnPreset
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ConfirmDialogDetailsBox, ConfirmDialogFrame } from "@/shared/components/ConfirmDialog";
import { foldText } from "@/shared/utils/text-search";
import { formatInteger } from "@/shared/utils/format";
import {
  EXPORT_BYTES_PER_ROW,
  EXPORT_CSV_SUGGESTION_ROWS,
  EXPORT_FORMAT_LABELS,
  EXPORT_FORMATS,
  type ExportFormat,
  type ExportThemeLayout
} from "@/shared/vocabulary/exports";
import {
  columnsForRequest,
  defaultKeys,
  initialSelection,
  orderedSelection,
  readColumnChoice,
  sanitizeColumns,
  withOrphanRaw,
  writeColumnChoice
} from "./export-columns";
import { formatFileSize } from "./export-view";

/** O aviso de recorte grande, ou `null` quando não há o que avisar.
 *  Puro e testado: o tamanho vem de `EXPORT_BYTES_PER_ROW` (medido) e o
 *  limiar de `EXPORT_CSV_SUGGESTION_ROWS`. */
export function bigExportWarning(totalRows: number | null | undefined): string | null {
  if (totalRows == null || totalRows <= EXPORT_CSV_SUGGESTION_ROWS) return null;
  const xlsx = formatFileSize(totalRows * EXPORT_BYTES_PER_ROW.xlsx);
  const csv = formatFileSize(totalRows * EXPORT_BYTES_PER_ROW.csv);
  return (
    `Este recorte tem ${formatInteger(totalRows)} linhas. O Excel abre um arquivo desse tamanho ` +
    `(cerca de ${xlsx}), mas devagar. O CSV (cerca de ${csv}) é maior, porém abre ` +
    "rápido em qualquer programa."
  );
}

/** O que o pop-up entrega: `columns` ausente = o colunas padrão. */
export type ExportChoice = {
  format: ExportFormat;
  columns?: string[];
  header: boolean;
  theme_layout: ExportThemeLayout;
  highlight_only: boolean;
};

const DEFAULT_GROUP_TITLE = "Colunas padrão";
const EXTRA_GROUP_TITLE = "Dados extras";
const RAW_GROUP_TITLE = "Planilha da clipadora";

/**
 * A escolha do formato — e, nas telas de amostra, menções e dashboard,
 * das COLUNAS — antes de pedir a planilha (2026-10-15; colunas e planilhas
 * salvas em 2026-11-01). Sem `columns`, é só o formato.
 *
 * - **Exportar sem mexer é o colunas padrão de hoje**: a seleção inicial
 *   é o grupo `default` inteiro e, intacta, o pedido não leva `columns`.
 * - **O catálogo e as planilhas salvas carregam UMA vez** ao abrir; falha
 *   de qualquer um vira aviso aqui dentro e o formato segue funcionando
 *   com as colunas padrão — nunca trava o exportar.
 * - **Clicar numa planilha salva aplica E exporta** na hora, com o
 *   formato escolhido.
 * - **Nada de formulário aninhado**: o apagar pede confirmação na própria
 *   linha (um `ConfirmDialog` por cima mandaria o `submit` dele subir
 *   pelo portal até este formulário e exportaria sem querer), e o Enter
 *   nos campos de texto não envia o pedido.
 */
export function ExportFormatDialog({
  token,
  columns: withColumns = false,
  canManagePresets = true,
  totalRows,
  busy = false,
  error,
  description,
  details,
  onConfirm,
  onCancel
}: {
  /** Obrigatório com `columns` (o catálogo e as planilhas salvas). */
  token?: string;
  /** Mostra a escolha de colunas e as planilhas salvas. */
  columns?: boolean;
  /** Técnico ou acima salva e apaga; o Visualizador só aplica. */
  canManagePresets?: boolean;
  /** Troca o parágrafo padrão (a planilha de outro assunto). */
  description?: string;
  /** Lista em destaque ("Fica de fora"). */
  details?: { title: string; items: string[] };
  /** Linhas do recorte, quando a tela sabe. */
  totalRows?: number | null;
  busy?: boolean;
  error?: string | null;
  onConfirm: (choice: ExportChoice) => void;
  onCancel: () => void;
}) {
  const [format, setFormat] = useState<ExportFormat>("xlsx");
  const [header, setHeader] = useState(true);
  const [themePair, setThemePair] = useState(false);
  const [highlightOnly, setHighlightOnly] = useState(false);
  const [catalog, setCatalog] = useState<ExportColumn[] | null>(null);
  const [presets, setPresets] = useState<ExportColumnPreset[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loadWarning, setLoadWarning] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [presetName, setPresetName] = useState("");
  const [presetError, setPresetError] = useState<string | null>(null);
  const [presetBusy, setPresetBusy] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const warning = bigExportWarning(totalRows);

  useEffect(() => {
    if (!withColumns || !token) return;
    let alive = true;
    void Promise.allSettled([
      apiClient.exportColumns(token),
      apiClient.listExportColumnPresets(token)
    ]).then(([columnsResult, presetsResult]) => {
      if (!alive) return;
      const problems: string[] = [];
      if (columnsResult.status === "fulfilled") {
        const start = initialSelection(columnsResult.value, readColumnChoice());
        const loaded = withOrphanRaw(columnsResult.value, [...start.selected]);
        setCatalog(loaded);
        setSelected(start.selected);
        setHeader(start.header);
        setThemePair(start.theme_layout === "pair");
        setHighlightOnly(start.highlight_only);
      } else {
        problems.push(
          `Não foi possível carregar as colunas (${errorMessage(columnsResult.reason, "erro")}); a planilha sai com as colunas padrão.`
        );
      }
      if (presetsResult.status === "fulfilled") {
        setPresets(presetsResult.value);
      } else {
        problems.push(
          `Não foi possível carregar as planilhas salvas (${errorMessage(presetsResult.reason, "erro")}).`
        );
      }
      setLoadWarning(problems.length > 0 ? problems.join(" ") : null);
    });
    return () => {
      alive = false;
    };
  }, [withColumns, token]);

  const visible = useMemo(() => {
    if (!catalog) return [];
    const needle = foldText(search.trim());
    if (!needle) return catalog;
    const words = needle.split(/\s+/);
    return catalog.filter(column => {
      const text = foldText(column.label);
      return words.every(word => text.includes(word));
    });
  }, [catalog, search]);

  const toggle = (key: string) =>
    setSelected(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const setMany = (keys: string[], on: boolean) =>
    setSelected(current => {
      const next = new Set(current);
      for (const key of keys) {
        if (on) next.add(key);
        else next.delete(key);
      }
      return next;
    });

  const noColumns = catalog !== null && selected.size === 0;

  const confirmWith = (
    columns: Set<string>,
    withHeader: boolean,
    pair: boolean,
    onlyHighlight: boolean,
    among: ExportColumn[] | null = catalog
  ) => {
    const theme_layout: ExportThemeLayout = pair ? "pair" : "columns";
    if (!among) {
      onConfirm({ format, header: withHeader, theme_layout, highlight_only: onlyHighlight });
      return;
    }
    writeColumnChoice({
      columns: orderedSelection(among, columns),
      header: withHeader,
      theme_layout,
      highlight_only: onlyHighlight
    });
    onConfirm({
      format,
      columns: columnsForRequest(among, columns),
      header: withHeader,
      theme_layout,
      highlight_only: onlyHighlight
    });
  };

  const useAndExport = (preset: ExportColumnPreset) => {
    if (!catalog) return;
    const kept = sanitizeColumns(catalog, preset.columns);
    if (kept.length === 0) {
      setPresetError("Nenhuma coluna desta planilha salva existe mais.");
      return;
    }
    setPresetError(null);
    const next = new Set(kept);
    const extended = withOrphanRaw(catalog, kept);
    setCatalog(extended);
    setSelected(next);
    setHeader(preset.header);
    const pair = preset.theme_layout === "pair";
    const onlyHighlight = preset.highlight_only === true;
    setThemePair(pair);
    setHighlightOnly(onlyHighlight);
    confirmWith(next, preset.header, pair, onlyHighlight, extended);
  };

  const savePreset = async () => {
    if (!token || !catalog) return;
    const name = presetName.trim();
    if (!name || selected.size === 0) return;
    setPresetBusy(true);
    setPresetError(null);
    try {
      const created = await apiClient.createExportColumnPreset(token, {
        name,
        columns: orderedSelection(catalog, selected),
        header,
        theme_layout: themePair ? "pair" : "columns",
        highlight_only: highlightOnly
      });
      setPresets(current => [...current, created]);
      setPresetName("");
    } catch (failure) {
      setPresetError(errorMessage(failure, "Não foi possível salvar a planilha."));
    } finally {
      setPresetBusy(false);
    }
  };

  const removePreset = async (preset: ExportColumnPreset) => {
    if (!token) return;
    setPresetBusy(true);
    setPresetError(null);
    try {
      await apiClient.deleteExportColumnPreset(token, preset.id);
      setPresets(current => current.filter(item => item.id !== preset.id));
      setDeleting(null);
    } catch (failure) {
      setPresetError(errorMessage(failure, "Não foi possível apagar a planilha."));
    } finally {
      setPresetBusy(false);
    }
  };

  const formatChoices = (
    <fieldset className="export-format-choices">
      <legend className="export-format-legend">Formato</legend>
      {EXPORT_FORMATS.map(option => (
        <label key={option} className="filter-check">
          <input
            type="radio"
            name="export-format"
            value={option}
            checked={format === option}
            onChange={() => setFormat(option)}
          />
          {EXPORT_FORMAT_LABELS[option]}
        </label>
      ))}
    </fieldset>
  );

  const renderGroup = (group: ExportColumn["group"], title: string) => {
    const rows = visible.filter(column => column.group === group);
    if (rows.length === 0) return null;
    return (
      <div className="export-columns-group" key={group}>
        <strong className="export-columns-group-title">{title}</strong>
        {rows.map(column => (
          <label key={column.key} className="filter-check export-column-item">
            <input
              type="checkbox"
              checked={selected.has(column.key)}
              onChange={() => toggle(column.key)}
            />
            <span>
              {column.label}
              {column.note && <small className="export-column-note">{column.note}</small>}
            </span>
          </label>
        ))}
      </div>
    );
  };

  return (
    <ConfirmDialogFrame
      title="Exportar planilha"
      icon={FileSpreadsheet}
      onClose={onCancel}
      onSubmit={() => confirmWith(selected, header, themePair, highlightOnly)}
      actions={
        <>
          <button type="button" className="secondary-button" onClick={onCancel} disabled={busy}>
            {withColumns ? "Fechar" : "Cancelar"}
          </button>
          <button type="submit" className="primary-button" disabled={busy || noColumns}>
            {busy ? (
              <LoaderCircle className="spin" size={16} />
            ) : withColumns ? (
              "Exportar"
            ) : (
              "Gerar planilha"
            )}
          </button>
        </>
      }
    >
      <div className={withColumns ? "export-dialog-wide" : undefined}>
        <p>
          {description ??
            "A planilha é gerada em segundo plano. Você será avisada no sino e o arquivo fica na página Downloads por 7 dias."}
        </p>
        {details && <ConfirmDialogDetailsBox details={details} />}
        {!withColumns && formatChoices}
        {withColumns && (
          <div className="export-columns-layout">
            <section className="export-columns-pane" aria-label="Colunas da planilha">
              <div className="export-columns-toolbar">
                <input
                  type="search"
                  className="export-columns-search"
                  placeholder="Buscar coluna"
                  aria-label="Buscar coluna"
                  value={search}
                  disabled={!catalog}
                  onChange={event => setSearch(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === "Enter") event.preventDefault();
                  }}
                />
                <button
                  type="button"
                  className="secondary-button export-columns-bulk"
                  disabled={!catalog}
                  onClick={() => setMany(visible.map(column => column.key), true)}
                >
                  Marcar todas
                </button>
                <button
                  type="button"
                  className="secondary-button export-columns-bulk"
                  disabled={!catalog}
                  onClick={() => setMany(visible.map(column => column.key), false)}
                >
                  Desmarcar todas
                </button>
              </div>
              {catalog ? (
                <>
                  <div className="export-columns-list">
                    {renderGroup("default", DEFAULT_GROUP_TITLE)}
                    {renderGroup("extra", EXTRA_GROUP_TITLE)}
                    {renderGroup("raw", RAW_GROUP_TITLE)}
                    {visible.length === 0 && (
                      <p className="export-columns-empty">Nenhuma coluna para esta busca.</p>
                    )}
                  </div>
                  <p className="ai-admin-caption export-columns-count">
                    {selected.size} de {catalog.length} colunas
                    {" · "}
                    <button
                      type="button"
                      className="export-columns-reset"
                      onClick={() => setSelected(new Set(defaultKeys(catalog)))}
                    >
                      Voltar às colunas padrão
                    </button>
                  </p>
                  <p className="ai-admin-caption">As colunas saem na ordem desta lista.</p>
                </>
              ) : (
                <p className="export-columns-empty">
                  {loadWarning ? "Colunas indisponíveis." : "Carregando as colunas…"}
                </p>
              )}
            </section>

            <div className="export-columns-side">
              <details className="export-presets" open>
                <summary>
                  <ChevronDown size={14} aria-hidden="true" />
                  Planilhas salvas ({presets.length})
                </summary>
                {presets.length === 0 && (
                  <p className="ai-admin-caption">Nenhuma planilha salva ainda.</p>
                )}
                <ul className="export-presets-list">
                  {presets.map(preset => (
                    <li key={preset.id}>
                      {deleting === preset.id ? (
                        <div
                          className="export-preset-confirm"
                          role="group"
                          aria-label={`Apagar ${preset.name}`}
                        >
                          <span>Apagar “{preset.name}”?</span>
                          <button
                            type="button"
                            className="secondary-button danger-button"
                            disabled={presetBusy}
                            onClick={() => void removePreset(preset)}
                          >
                            Apagar
                          </button>
                          <button
                            type="button"
                            className="secondary-button"
                            disabled={presetBusy}
                            onClick={() => setDeleting(null)}
                          >
                            Manter
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="export-preset-use"
                            disabled={busy || !catalog}
                            onClick={() => useAndExport(preset)}
                          >
                            <strong>{preset.name}</strong>
                            <small>Clique para exportar</small>
                          </button>
                          {canManagePresets && (
                            <button
                              type="button"
                              className="export-preset-remove"
                              aria-label={`Apagar a planilha salva ${preset.name}`}
                              disabled={busy}
                              onClick={() => setDeleting(preset.id)}
                            >
                              <X size={14} aria-hidden="true" />
                            </button>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
                {canManagePresets && (
                  <div className="export-preset-save">
                    <input
                      type="text"
                      aria-label="Nome da planilha salva"
                      placeholder="Nome da planilha"
                      maxLength={80}
                      value={presetName}
                      disabled={!catalog}
                      onChange={event => setPresetName(event.target.value)}
                      onKeyDown={event => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          void savePreset();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={presetBusy || !catalog || !presetName.trim() || selected.size === 0}
                      onClick={() => void savePreset()}
                    >
                      Salvar seleção atual como…
                    </button>
                  </div>
                )}
                {presetError && (
                  <div className="account-feedback error" aria-live="polite">
                    {presetError}
                  </div>
                )}
              </details>

              {formatChoices}

              <label className="export-header-toggle">
                <input
                  type="checkbox"
                  role="switch"
                  checked={header}
                  onChange={event => setHeader(event.target.checked)}
                />
                <span>Incluir nomes das colunas na primeira linha</span>
              </label>

              <fieldset className="export-subthemes">
                <legend className="export-format-legend">Subtemas</legend>
                <label className="export-header-toggle">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={themePair}
                    onChange={event => setThemePair(event.target.checked)}
                  />
                  <span>
                    Tema e subtemas em duas colunas
                    <small className="export-column-note">
                      No lugar de uma coluna por tema, saem as colunas “Tema” e “Subtemas”
                      (vários separados por “;”).
                    </small>
                  </span>
                </label>
                <label className="export-header-toggle">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={highlightOnly}
                    onChange={event => setHighlightOnly(event.target.checked)}
                  />
                  <span>
                    Só o subtema em destaque
                    <small className="export-column-note">Menção sem destaque sai vazia.</small>
                  </span>
                </label>
              </fieldset>
            </div>
          </div>
        )}
        {warning && (
          <div className="account-feedback warning" role="note">
            {warning}
          </div>
        )}
        {loadWarning && (
          <div className="account-feedback warning" role="note">
            {loadWarning}
          </div>
        )}
        {error && (
          <div className="account-feedback error" aria-live="polite">
            {error}
          </div>
        )}
      </div>
    </ConfirmDialogFrame>
  );
}
